import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { SizeFunction, TableVirtuoso } from 'react-virtuoso';
import { Stack } from 'src/core/ui';

import { itemSizeDefault } from '../Table/Table.utils';
import { DeferredRowContent } from './components/DeferredRowContent';
import * as styles from './components/Table.css';
import { TableProvider } from './components/TableContext';
import { TablePagination } from './components/TablePagination';
import { VirtuosoTable as VirtuosoTableComponent } from './components/VirtuosoTable';
import { VirtuosoTableContext } from './components/VirtuosoTableContext';
import { VirtuosoTableFooter } from './components/VirtuosoTableFooter';
import { VirtuosoTableHead } from './components/VirtuosoTableHead';
import { VirtuosoTableRow } from './components/VirtuosoTableRow';
import { useTableSetup } from './hooks/useTableSetup';
import {
  RowWrapper,
  RowWrapperV2,
  TablePaginationConfig,
  TableRowHeightVariant,
} from './types';

// Reduce overscan to decrease the number of rendered items during scrolling
const virtuosoTableOverscan = { main: 300, reverse: 300 };
const virtuosoTableStyle = { width: '100%', height: '100%' };

// Memoized TableVirtuoso components to prevent re-creation
const memoizedVirtuosoComponents = {
  Table: VirtuosoTableComponent,
  TableHead: VirtuosoTableHead,
  TableRow: VirtuosoTableRow,
  TableFoot: VirtuosoTableFooter,
};

// VirtuosoTable component for large datasets with virtualization
interface VirtuosoTableProps<T extends Record<string, any>> {
  actionColId?: string;
  data: T[];
  columns: ColumnDef<T>[];
  variant?: TableRowHeightVariant;
  bordered?: boolean;
  // Checkbox selection props
  alwaysShowCheckbox?: boolean;
  showSelectAllCheckbox?: boolean;
  getGroupId?: (data: T) => string;
  getItemId?: (data: T) => string | undefined;
  getRowId?: (data: T, index: number) => string;
  isSelectionDisabled?: (data: T) => string | undefined;
  onRowSelectionChange?: (updater: Updater<RowSelectionState>) => void;
  onExpandedChange?: (updater: any) => void; // For expandable rows
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  onFilteredRowsChange?: (rows: Row<T | null>[] | undefined) => void;
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  enableSubRowSelection?: boolean;
  // Virtuoso-specific props
  itemSize?: SizeFunction;
  overscan?: { main: number; reverse: number };
  onEndReached?: (lastItemIndex?: number) => void;
  onRowShiftSelect?: (
    tableRows: Row<T | null>[] | undefined,
    row: Row<T | null>,
    index: number
  ) => void;
  // Other props
  pagination?: TablePaginationConfig;
  className?: string;
  onRowClick?: (row: T, index: number) => void;
  summaryData?: T | null;
  style?: React.CSSProperties;
  state?: Partial<TableState>; // TanStack Table state
  sortingFns?: Record<string, any>; // Custom sorting functions
  meta?: Record<string, any>; // Table meta data
  footerCta?: React.ReactNode;
  rowWrapper?: RowWrapper<T>;
  rowWrapperV2?: RowWrapperV2<T>;
  getSubRows?: (row: T) => T[]; // For expandable rows
  getRowCanExpand?: (row: { original: T }) => boolean; // For expandable rows
  manualPagination?: boolean;
  rowCount?: number;
  enableColumnFilters?: boolean;
}

export const VirtuosoTable = <T extends Record<string, any>>({
  actionColId,
  data,
  columns,
  variant = 'normal',
  alwaysShowCheckbox,
  getGroupId,
  getRowId,
  getItemId,
  isSelectionDisabled,
  onRowSelectionChange,
  onExpandedChange,
  onPaginationChange,
  onSortingChange,
  onFilteredRowsChange,
  enableRowSelection,
  enableMultiRowSelection,
  enableSubRowSelection,
  itemSize,
  overscan,
  onEndReached,
  onRowShiftSelect,
  pagination,
  className,
  onRowClick,
  summaryData,
  state,
  sortingFns,
  meta,
  footerCta,
  rowWrapper,
  rowWrapperV2,
  getSubRows,
  getRowCanExpand,
  manualPagination,
  rowCount,
  enableColumnFilters,
}: VirtuosoTableProps<T>) => {
  const { table, renderHeaderGroups, renderRowContent, renderFooterRows } =
    useTableSetup({
      actionColId,
      data,
      columns,
      alwaysShowCheckbox,
      getGroupId,
      getItemId,
      getRowId:
        getRowId ||
        (getItemId
          ? (data, index) => getItemId(data) ?? String(index)
          : undefined),
      isSelectionDisabled,
      onRowSelectionChange,
      onExpandedChange,
      onPaginationChange,
      onSortingChange,
      onFilteredRowsChange,
      enableRowSelection,
      enableMultiRowSelection,
      enableSubRowSelection,
      pagination,
      summaryData,
      state,
      sortingFns,
      meta,
      getSubRows,
      getRowCanExpand,
      manualPagination,
      rowCount,
      enableColumnFilters,
    });

  const rows = table.getRowModel().rows;
  const columnWidths = useMemo(
    () => table.getVisibleLeafColumns().map((col) => col.getSize()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, table.getState().columnSizing]
  );
  const columnSizingState = table.getState().columnSizing;

  const virtuosoContext = useMemo<VirtuosoTableContext<T>>(
    () => ({
      table,
      columnSizing: columnSizingState,
      variant,
      rows,
      onRowClick,
      onRowShiftSelect,
    }),
    [table, variant, rows, columnSizingState, onRowClick, onRowShiftSelect]
  );

  // Memoize item content renderer using shared row renderer.
  // For rowWrapperV2, wraps in DeferredRowContent — new rows show shimmer instantly
  // during fast scroll, then swap in real content on the next frame.
  const itemContentRenderer = useCallback(
    (index: number, row: any) => {
      if (!row) return null;

      if (rowWrapperV2) {
        return (
          <DeferredRowContent
            columnWidths={columnWidths}
            renderContent={rowWrapperV2(row, index, renderRowContent)}
          />
        );
      }

      const rowContent = renderRowContent(row as Row<T>);
      return rowWrapper
        ? rowWrapper((row as Row<T>).original, rowContent, index)
        : rowContent;
    },
    [columnWidths, renderRowContent, rowWrapper, rowWrapperV2]
  );
  return (
    <TableProvider value={table}>
      <div
        className={clsx(
          styles.tableContainer,
          styles.tableBordered,
          styles.POSTableVirtuosoContainer,
          className
        )}
      >
        <TableVirtuoso<any, VirtuosoTableContext<T>>
          style={virtuosoTableStyle}
          data={rows}
          overscan={overscan ?? virtuosoTableOverscan}
          context={virtuosoContext}
          endReached={onEndReached}
          itemSize={itemSize ?? itemSizeDefault}
          components={memoizedVirtuosoComponents as any}
          fixedHeaderContent={renderHeaderGroups}
          fixedFooterContent={renderFooterRows}
          itemContent={itemContentRenderer}
        />
        <Stack justifyContent="spaceBetween">
          {pagination && (
            <TablePagination
              table={table}
              pageSize={pagination.pageSize}
              variant={pagination.variant}
              totalCount={pagination.totalCount}
            />
          )}
          {footerCta}
        </Stack>
      </div>
    </TableProvider>
  );
};
